import type WebSocket from 'ws';
import type { MessageHandlerDeps } from '../../../routing/message-handler-deps.js';
import { sendWsMessage } from '../../../connection/create-ws-bridge.js';
import type { BridgeToApiMessage, BridgeToApiPromptResultMessage } from '../../../types/bridge-to-api-messages.js';
import { finalizePromptTurnOnBridge } from '../../../prompt-turn-queue/runner.js';

export type BridgePromptSenders = ReturnType<typeof createBridgePromptSenders>;

export function createBridgePromptSenders(deps: MessageHandlerDeps, getWs: () => WebSocket | null) {
  const sendBridgeMessage = (message: BridgeToApiMessage, encryptedFields: readonly string[] = []) => {
    const s = getWs();
    if (!s) return false;
    const wire: BridgeToApiMessage =
      deps.e2ee && encryptedFields.length > 0
        ? (deps.e2ee.encryptFields(message as Record<string, unknown>, encryptedFields) as BridgeToApiMessage)
        : message;
    sendWsMessage(s, wire);
    return true;
  };

  const sendResult = (result: BridgeToApiPromptResultMessage) => {
    const encryptedFields = result.type === 'prompt_result' ? (['output', 'error'] as const) : [];
    sendBridgeMessage(result, encryptedFields);
    if (result.type === 'prompt_result') {
      const pr = result as BridgeToApiPromptResultMessage & { stopReason?: string };
      const cancelled = pr.stopReason === 'cancelled';
      void finalizePromptTurnOnBridge(
        getWs,
        typeof pr.runId === 'string' ? pr.runId : undefined,
        pr.success === true,
        cancelled ? { terminalCliState: 'cancelled' } : undefined,
      ).catch(() => {
        /* ignore queue persistence errors */
      });
    }
  };

  const sendSessionUpdate = (payload: unknown) => {
    const s = getWs();
    if (!s) {
      deps.log('[Bridge service] Session update not sent: not connected to the bridge.');
      return;
    }
    const p = payload as {
      type?: string;
      runId?: string;
      kind?: string;
      path?: string;
      patchContent?: string;
    };
    const wire: BridgeToApiMessage =
      p.type === 'session_update' && deps.e2ee
        ? (deps.e2ee.encryptFields(payload as Record<string, unknown>, ['payload']) as BridgeToApiMessage)
        : p.type === 'session_file_change' && deps.e2ee
          ? (deps.e2ee.encryptFields(payload as Record<string, unknown>, [
              'path',
              'oldText',
              'newText',
              'patchContent',
              'isDirectory',
              'directoryRemoved',
            ]) as BridgeToApiMessage)
          : (payload as BridgeToApiMessage);
    sendWsMessage(s, wire);
  };

  return { sendBridgeMessage, sendResult, sendSessionUpdate };
}
